<template>
    <div :class="[ 'table', { 'table--margin': !settings.removeMargin } ]" :style="`--max-height: ${settings.maxHeight || '300px'}`">
        <transition name="fade" mode="out-in">
        <div v-if="list.length" class="table--with-results" aria-label="table">
            <div class="table__header" role="rowgroup">
                <div class="table__header__row" role="row">
                    <column-header
                        v-for="( header, index ) in headers"
                        :header="header"
                        class="table__header__row__text display--inline-b capitalize"
                        :class="`table__${model}__col__${index}`"
                        :key="index"
                    />
                </div>
            </div>
            <div class="table__body scroll" role="rowgroup">
                <component
                    :is="component"
                    v-for="( row, indexField ) in list"
                    :class="[
                        'table__body__row display--flex',
                        { 'table__body__row--color': indexField % 2 === 0 }
                    ]"
                    :headers="headers"
                    :row="row"
                    :model="model"
                    :key="row.uuid || row.id"
                    v-bind="{
                        ...( settings.action && { action: settings.action } )
                    }"
                />
            </div>
        </div>
        <div v-else class="table--without-results">{{ $t('global.noRecordsFound', { type: settings.warning} ) }}</div>
        </transition>
    </div>
</template>

<script>
export default {
    components: {
        ColumnHeader: () => import( /* webpackChunkName: 'ColumnHeader' */ '@/components/bundles/tables/packs/ColumnHeader' )
    },
    props: {
        headers: Array,
        list: Array,
        model: String,
        settings: Object
    },
    setup ( props ) {
        const component = () => import( /* webpackChunkName: 'Row-[request]' */ `@/components/dynamic/rows/${props.settings.component}` );

        return { component };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $columns-calls: ( '0': 10%, '1': 20%, '2': 15%, '3': 15%, '4': 24%, '5': 8%, '6': 8% );
    $columns-call-rules: ( '0': 22%, '1': 17%, '2': 17%, '3': 8%, '4': 15%, '5': 17%, '6': 4% );
    $columns-history-calls: ( '0': 20%, '1': 30%, '2': 50% );
    $columns-tokens: ( '0': 25%, '1': 30%, '2': 20%, '3': 20%, '4': 5% );
    $columns-scripts: ( '0': 70%, '1': calc( 30% - 102px ), '2': 100px );
    $columns-mail-templates: ( '0': calc( 100% - 62px ), '1': 62px );
    $columns-requests: ( '0': 25%, '1': 25%, '2': 10%, '3': 23%, '4': 10%, '5': 7% );
    $columns-entities: ( '0': 10%, '1': 25%, '2': 15%, '3': 16.6%, '4': 16.6%, '5': 16.6% );

    // =============================================================================
    // Styles
    // =============================================================================
    .table {
        &--margin {
            margin-top: $card-space-between-header-body;
        }
        &--without-results {
            padding: $card-spacing;
        }
        &__header {
            padding: $card-spacing;
            &__row {
                @include typography-specs( 20px, 26px, 500 );
            }
        }
        &__body {
            max-height: var( --max-height );
            margin-top: 5px;
            overflow-x: auto;
            &__row {
                align-items: center;
                height: 60px;
                padding: $card-spacing;
                &--color {
                    background: $bg-color;
                }
                &__text {
                    @include typography-specs( 18px, 24px, 400 );
                    &__svg {
                        margin-left: 10px;
                    }
                    &__tooltip {
                        @include transform-position( 1px, false, false, -96px, translateX( -50% ) );
                        width: max-content;
                    }
                }
            }
        }
        &__contact {
            &__col {
                @each $index,
                $width in $columns-calls {
                    &__#{$index} {
                        width: $width;
                         @if ($index == '4' ) {
                            &.table__body__row__text {
                                .audio {
                                    width: 100%;
                                    max-width: 300px;
                                    &__player {
                                        &::before {
                                            width: calc( 100% - 65px );
                                        }
                                    }
                                }
                            }
                        }
                         @if ($index == '5' ) {
                            &.table__header__row__text {
                                margin-left: -50px;
                            }
                        }
                        @if ( $index == '6' ) {
                            &.table__header__row__text {
                                display: none;
                            }
                        }
                    }
                }
            }
        }
        &__history {
            &__col {
                @each $index,
                $width in $columns-history-calls {
                    &__#{$index} {
                        width: $width;
                         @if ( $index == '3' ) {
                            &.table__header__row__text {
                                display: none;
                            }
                        }
                    }
                }
            }
        }
        &__token {
            &__col {
                @each $index,
                $width in $columns-tokens {
                    &__#{$index} {
                        width: $width;
                        @if $index == '0' or $index == '1' {
                            overflow: hidden;
                            white-space: nowrap;
                            text-overflow: ellipsis;
                            padding-right: 40px;
                        }
                    }
                }
            }
        }
        &__script {
            &__col {
                @each $index,
                $width in $columns-scripts {
                    &__#{$index} {
                        width: $width;
                    }
                }
            }
        }
        &__rules {
            &__col {
                @each $index,
                $width in $columns-call-rules {
                    &__#{$index} {
                        width: $width;
                    }
                }
            }
        }
        &__mailTemplates {
            &__col {
                @each $index,
                $width in $columns-mail-templates {
                    &__#{$index} {
                        width: $width;
                    }
                }
            }
        }
        &__request {
            &__col {
                @each $index,
                $width in $columns-requests {
                    &__#{$index} {
                        width: $width;
                    }
                }
            }
        }
        &__entity {
            &__col {
                @each $index,
                $width in $columns-entities {
                    &__#{$index} {
                        width: $width;
                    }
                }
            }
        }
    }
</style>